// "Print on which letterhead?" — shown on Print of a Sale Invoice / Non-Tax
// Sale Invoice when the Invoice Letterhead Choice module is on
// (utils/invoiceLetterheads.js). Remembers the last choice on this device.
import React, { useState } from 'react';
import styled from 'styled-components';
import { FiPrinter } from 'react-icons/fi';
import { colors } from '../theme/colors';
import useEscapeClose from '../hooks/useEscapeClose';
import { lastLetterheadChoice, rememberLetterheadChoice } from '../utils/invoiceLetterheads';

const LetterheadPicker = ({ companyId, companyName, letterheads, onPrint, onClose }) => {
  const [choice, setChoice] = useState(() => {
    const last = lastLetterheadChoice(companyId);
    return (last === 'company' || last === 'blank' || (letterheads || []).some((l) => l.id === last)) ? last : 'company';
  });
  useEscapeClose(true, onClose);

  const options = [
    { id: 'company', title: companyName || 'Company', sub: 'Company details (as usual)' },
    ...(letterheads || []).map((l) => ({ id: l.id, title: l.name, sub: [l.address, l.phone].filter(Boolean).join(' · ') })),
    { id: 'blank', title: 'Blank', sub: 'No company details at the top' },
  ];

  const go = () => {
    rememberLetterheadChoice(companyId, choice);
    onPrint(choice);
  };

  return (
    <Overlay onClick={onClose}>
      <Modal role="dialog" aria-label="Print letterhead" onClick={(e) => e.stopPropagation()}>
        <Header>
          <h3>Print on which letterhead?</h3>
          <CloseBtn type="button" aria-label="Close" onClick={onClose}>×</CloseBtn>
        </Header>
        <Body>
          {options.map((o) => (
            <Option key={o.id} $on={choice === o.id}>
              <input type="radio" name="letterheadChoice" value={o.id} checked={choice === o.id} onChange={() => setChoice(o.id)} />
              <span>
                <strong>{o.title}</strong>
                {o.sub ? <small>{o.sub}</small> : null}
              </span>
            </Option>
          ))}
        </Body>
        <Footer>
          <CancelBtn type="button" onClick={onClose}>Cancel</CancelBtn>
          <PrintBtn type="button" onClick={go}><FiPrinter /> Print</PrintBtn>
        </Footer>
      </Modal>
    </Overlay>
  );
};

export default LetterheadPicker;

const Overlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.45);
  display: flex; align-items: center; justify-content: center; z-index: 3000;
`;
const Modal = styled.div`
  background: #fff; border-radius: 10px; width: 440px; max-width: 94vw;
  box-shadow: 0 12px 40px rgba(0,0,0,0.25); overflow: hidden;
`;
const Header = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 18px; border-bottom: 1px solid #eee;
  h3 { margin: 0; font-size: 16px; color: ${colors.maroon || '#800020'}; }
`;
const CloseBtn = styled.button`
  border: none; background: none; font-size: 22px; line-height: 1; cursor: pointer; color: #888;
`;
const Body = styled.div`padding: 14px 18px; max-height: 60vh; overflow-y: auto;`;
const Option = styled.label`
  display: flex; align-items: flex-start; gap: 10px; padding: 10px 12px; margin-bottom: 8px;
  border-radius: 8px; cursor: pointer;
  border: ${(p) => (p.$on ? `2px solid ${colors.maroon || '#800020'}` : '1px solid #ddd')};
  background: ${(p) => (p.$on ? '#80002008' : '#fff')};
  input { margin-top: 3px; cursor: pointer; }
  span { display: flex; flex-direction: column; gap: 2px; font-size: 14px; color: #222; }
  small { font-size: 12px; color: #777; }
`;
const Footer = styled.div`
  display: flex; justify-content: flex-end; gap: 10px; padding: 14px 18px; border-top: 1px solid #eee;
`;
const CancelBtn = styled.button`
  padding: 8px 16px; border: 1px solid #ccc; background: #fff; border-radius: 6px; cursor: pointer;
`;
const PrintBtn = styled.button`
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 18px; border: none; background: ${colors.maroon || '#800020'}; color: #fff;
  border-radius: 6px; cursor: pointer; font-weight: 600;
`;
